// OwnerEarningsCard.tsx
import React, { useState } from "react";
import { OwnerEarningsCardProps } from "../types/revenue.types";
import RevenueService from "../../../services/revenueService";

export const OwnerEarningsCard: React.FC<OwnerEarningsCardProps> = ({
  owner,
  colorTheme,
}) => {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <div
      style={{
        background: colorTheme.cardBg,
        borderRadius: "16px",
        padding: "24px",
        border: "1px solid #f1f5f9",
        marginBottom: "16px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
      }}
    >
      {/* Owner Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: colorTheme.gray900,
              margin: "0 0 4px 0",
            }}
          >
            👑 {owner.owner_name}
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: colorTheme.gray600,
              margin: "0 0 8px 0",
            }}
          >
            {owner.owner_role}
          </p>
          <div style={{ display: "flex", gap: "16px", fontSize: "12px" }}>
            <span style={{ color: colorTheme.gray500 }}>
              {owner.transactions_count} equipment uses
            </span>
            <span style={{ color: colorTheme.gray500 }}>
              {owner.equipment_used.length} equipment types
            </span>
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "700",
              color: colorTheme.warning,
            }}
          >
            {RevenueService.formatCurrency(owner.total_earned)}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: colorTheme.gray500,
              marginTop: "4px",
            }}
          >
            Equipment earnings
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px",
          marginBottom: "16px",
          padding: "16px",
          background: colorTheme.gray50,
          borderRadius: "12px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.warning,
            }}
          >
            {RevenueService.formatCurrency(owner.avg_per_transaction)}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Avg per Use
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.success,
            }}
          >
            {owner.transactions_count}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Total Uses
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.primary,
            }}
          >
            {owner.equipment_used.length}
          </div>
          <div style={{ fontSize: "12px", color: colorTheme.gray600 }}>
            Equipment Types
          </div>
        </div>
      </div>

      {/* Equipment Breakdown Toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: "100%",
          padding: "12px",
          background: expanded ? colorTheme.warning : colorTheme.gray100,
          color: expanded ? "white" : colorTheme.gray700,
          border: "none",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "14px",
          fontWeight: "500",
          transition: "all 0.3s ease",
        }}
      >
        {expanded ? "Hide" : "Show"} Equipment Breakdown {expanded ? "▲" : "▼"}
      </button>

      {/* Expanded Equipment Details */}
      {expanded && (
        <div
          style={{
            marginTop: "16px",
            padding: "16px",
            background: colorTheme.gray50,
            borderRadius: "8px",
          }}
        >
          <h4
            style={{
              fontSize: "14px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Equipment Usage
          </h4>
          {owner.equipment_used.map((equipment: any, index: number) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
                borderBottom:
                  index < owner.equipment_used.length - 1
                    ? `1px solid ${colorTheme.gray200}`
                    : "none",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    fontWeight: "500",
                    color: colorTheme.gray900,
                  }}
                >
                  {equipment.equipment_name}
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: colorTheme.gray600,
                  }}
                >
                  {equipment.equipment_type} • {equipment.usage_count} uses •{" "}
                  {RevenueService.formatCurrency(equipment.avg_per_use)} per use
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: colorTheme.warning,
                  }}
                >
                  {RevenueService.formatCurrency(equipment.earned)}
                </div>
              </div>
            </div>
          ))}

          {/* Services Using Owner's Equipment */}
          {owner.services_used.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Services Using Equipment
              </h4>
              {owner.services_used
                .slice(0, 5)
                .map((service: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{ color: colorTheme.gray900, fontWeight: "500" }}
                      >
                        {service.service_name}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, margin: "0 8px" }}
                      >
                        •
                      </span>
                      <span style={{ color: colorTheme.gray600 }}>
                        {service.count} times
                      </span>
                    </div>
                    <div>
                      <span
                        style={{ color: colorTheme.warning, fontWeight: "600" }}
                      >
                        {RevenueService.formatCurrency(service.earned)}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Recent Equipment Usage */}
          {owner.recent_usages.length > 0 && (
            <div style={{ marginTop: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: colorTheme.gray900,
                  marginBottom: "12px",
                }}
              >
                Recent Equipment Usage
              </h4>
              {owner.recent_usages
                .slice(0, 3)
                .map((usage: any, index: number) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 0",
                      fontSize: "12px",
                    }}
                  >
                    <div>
                      <span
                        style={{ color: colorTheme.gray900, fontWeight: "500" }}
                      >
                        {usage.patient_name}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, margin: "0 4px" }}
                      >
                        •
                      </span>
                      <span style={{ color: colorTheme.gray600 }}>
                        {usage.equipment}
                      </span>
                    </div>
                    <div>
                      <span
                        style={{ color: colorTheme.warning, fontWeight: "600" }}
                      >
                        {RevenueService.formatCurrency(usage.earned)}
                      </span>
                      <span
                        style={{ color: colorTheme.gray500, marginLeft: "8px" }}
                      >
                        {new Date(usage.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.warning,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          💰 Process Payment
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.primary,
            color: "white",
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          🏭 Equipment Report
        </button>
        <button
          style={{
            flex: 1,
            padding: "10px",
            background: colorTheme.gray200,
            color: colorTheme.gray700,
            border: "none",
            borderRadius: "6px",
            fontSize: "12px",
            fontWeight: "500",
            cursor: "pointer",
          }}
        >
          📧 Send Report
        </button>
      </div>
    </div>
  );
};
